import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TextField,
} from "@mui/material";
import { MdCheckCircle, MdFilterList, MdRefresh } from "react-icons/md";
import { BiCreditCard } from "react-icons/bi";

import { CustomTable } from "./index.js";
import { useAppContext } from "../contexts/AppContextProvider.js";
import { isoTimestampToReadable } from "../utils/timeCalculator.js";

const EMPTY_FILTERS = {
  search: "",
  channelName: "all",
  countryCode: "all",
  entityType: "all",
  includeChannelPricing: "all",
};
const text = (value) => String(value ?? "").trim();
const number = (value) => Number(value ?? 0).toLocaleString("en-IN");
const money = (value, symbol = "", code = "") =>
  `${text(symbol) || text(code)}${text(symbol) ? "" : " "}${Number(value ?? 0).toLocaleString("en-IN")}`.trim();

const Pricing = () => {
  const { partnerPricingData, getPartnerPricingData } = useAppContext();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedUsers, setExpandedUsers] = useState({});
  const channelPricing = partnerPricingData?.channelPricing ?? [];
  const userPricing = partnerPricingData?.userPricing ?? [];

  useEffect(() => {
    getPartnerPricingData().catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const matches = (row, includeFilter = true) => {
    const query = filters.search.toLowerCase();
    const searchable = [
      row.corporate_name,
      row.business_name_c,
      row.username,
      row.channel_name,
      row.country_code,
      row.entity_type,
    ]
      .map(text)
      .join(" ")
      .toLowerCase();
    return (
      (!query || searchable.includes(query)) &&
      (filters.channelName === "all" ||
        row.channel_name === filters.channelName) &&
      (filters.countryCode === "all" ||
        row.country_code === filters.countryCode) &&
      (filters.entityType === "all" ||
        row.entity_type === filters.entityType) &&
      (!includeFilter ||
        filters.includeChannelPricing === "all" ||
        String(row.is_include_channel_pricing) ===
          filters.includeChannelPricing)
    );
  };
  const filteredChannelPricing = useMemo(
    () => channelPricing.filter((row) => matches(row, false)),
    [channelPricing, filters],
  ); // eslint-disable-line react-hooks/exhaustive-deps
  const filteredUserPricing = useMemo(() => {
    const unique = new Map();
    userPricing
      .filter((row) => matches(row))
      .forEach((row) => {
        const key = [
          row.user_id,
          row.channel_name,
          row.user_channel_id,
          row.entity_type,
          row.country_code,
        ].join("|");
        const previous = unique.get(key);
        if (
          !previous ||
          Number(row.platform_fee_id) > Number(previous.platform_fee_id)
        )
          unique.set(key, row);
      });
    return [...unique.values()];
  }, [userPricing, filters]); // eslint-disable-line react-hooks/exhaustive-deps
  const groupedUsers = useMemo(() => {
    const groups = new Map();
    filteredUserPricing.forEach((row) => {
      const key = String(row.user_id ?? row.corporate_name ?? "unknown");
      if (!groups.has(key))
        groups.set(key, {
          key,
          userId: row.user_id,
          corporateName: row.corporate_name || "Unnamed business",
          businessName: row.business_name_c,
          username: row.username,
          currency: row.user_currency,
          symbol: row.user_currency_symbol,
          rows: [],
        });
      groups.get(key).rows.push(row);
    });
    return [...groups.values()];
  }, [filteredUserPricing]);
  const options = (field) =>
    [
      ...new Set(
        [...channelPricing, ...userPricing]
          .map((row) => text(row[field]))
          .filter(Boolean),
      ),
    ].sort();
  const toggleUser = (key) =>
    setExpandedUsers((current) => ({ ...current, [key]: !current[key] }));
  const channelColumns = [
    { field: "channel_name", headerName: "Channel", width: 160 },
    { field: "entity_type", headerName: "Entity type", width: 140 },
    { field: "country_code", headerName: "Country", width: 110 },
    {
      field: "price",
      headerName: "Reference price",
      width: 160,
      valueGetter: (value, row) =>
        money(value, row.currency_symbol, row.currency_code),
    },
    { field: "currency_code", headerName: "Currency", width: 110 },
    {
      field: "price_wef",
      headerName: "Effective from",
      width: 180,
      valueGetter: (value) => (value ? isoTimestampToReadable(value) : "—"),
    },
    { field: "remarks", headerName: "Notes", width: 220 },
  ];

  return (
    <main className="space-y-6 p-5">
      <section className="relative overflow-hidden rounded-2xl border border-borderColor bg-backgroundPrimary p-6 shadow-sm lg:p-7">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-accent">
              <BiCreditCard size={18} /> Partner operations
            </div>
            <h2>Pricing configuration</h2>
            <p className="mt-2 max-w-3xl text-textSecondary">
              Review reference communication prices and the effective pricing
              rules configured for each user.
            </p>
          </div>
          <button
            type="button"
            onClick={() => getPartnerPricingData().catch(() => {})}
            className="flex items-center gap-2 rounded-lg border border-borderColor px-4 py-2 text-sm font-semibold text-textPrimary hover:bg-backgroundPrimary"
          >
            <MdRefresh size={19} /> Refresh
          </button>
        </div>
        <div className="mt-6 grid gap-3 md:grid-cols-3">
          {[
            [
              "Reference channels",
              filteredChannelPricing.length,
              "text-accent",
            ],
            ["Configured users", groupedUsers.length, "text-blue-300"],
            ["Pricing rules", filteredUserPricing.length, "text-emerald-300"],
          ].map(([label, value, color]) => (
            <div
              key={label}
              className="rounded-xl border border-borderColor bg-backgroundPrimary p-4"
            >
              <p className="text-xs font-bold uppercase tracking-wider text-textSecondary">
                {label}
              </p>
              <p className={`mt-2 text-2xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-borderColor bg-backgroundPrimary p-5 space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setShowFilters((value) => !value)}
            className="flex items-center gap-2  bg-accent px-3 py-2 text-sm font-semibold text-white"
          >
            <MdFilterList size={19} /> Filters
          </button>
          <TextField
            size="small"
            type="text"
            label="Search users, channels, countries"
            value={filters.search}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                search: event.target.value,
              }))
            }
            sx={{
              "& .MuiInputLabel-root": {
                color: "var(--text-primary)",
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "var(--text-primary)",
              },
              "& .MuiOutlinedInput-root": {
                color: "var(--text-primary)",
                "& fieldset": {
                  borderColor: "var(--border-color)",
                },
                "&:hover fieldset": {
                  borderColor: "var(--border-color)",
                },
                "&.Mui-focused": {
                  color: "var(--text-primary)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "var(--text-primary)",
                },
                "& input": {
                  color: "var(--text-primary)",
                },
                "& input:focus": {
                  color: "var(--text-primary)",
                },
              },
            }}
          />
          <span className="ml-auto text-sm text-textSecondary">
            {filteredUserPricing.length} rules across {groupedUsers.length}{" "}
            users
          </span>
        </div>
        {showFilters && (
          <Box
            className="mt-4 rounded-xl border border-borderColor bg-backgroundPrimary p-4"
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1.5,
            }}
          >
            {[
              ["channelName", "Channel", "channel_name"],
              ["countryCode", "Country", "country_code"],
              ["entityType", "Entity type", "entity_type"],
            ].map(([key, label, field]) => (
              <FormControl key={key} fullWidth size="small">
                <InputLabel
                  id={`${key}-filter-label`}
                  sx={{
                    color: "var(--text-primary)",
                    "&.Mui-focused": {
                      color: "var(--text-primary)",
                    },
                  }}
                >
                  {label}
                </InputLabel>

                <Select
                  labelId={`${key}-filter-label`}
                  label={label}
                  value={filters[key] ?? ""}
                  onChange={(event) =>
                    setFilters((current) => ({
                      ...current,
                      [key]: event.target.value,
                    }))
                  }
                  displayEmpty={false}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: "var(--background-primary)",
                        color: "var(--text-primary)",
                        "& .MuiMenuItem-root": {
                          background: "var(--background-primary)",
                          color: "var(--text-primary)",
                        },
                        "& .MuiMenuItem-root:hover": {
                          background: "var(--background-secondary)",
                        },
                        "& .MuiMenuItem-root.Mui-selected": {
                          background: "var(--background-secondary)",
                        },
                        "& .MuiMenuItem-root.Mui-selected:hover": {
                          background: "var(--background-secondary)",
                        },
                      },
                    },
                  }}
                  sx={{
                    background: "var(--background-primary)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "var(--border-color)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "var(--border-color)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "var(--text-primary)",
                    },
                    "& .MuiSelect-select": {
                      color: "var(--text-primary)",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "var(--text-primary)",
                    },
                  }}
                >
                  <MenuItem value="all">All</MenuItem>

                  {options(field).map((value) => (
                    <MenuItem key={String(value)} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}

            <FormControl fullWidth size="small">
              <InputLabel
                id="include-channel-pricing-label"
                sx={{
                  color: "var(--text-primary)",
                  "&.Mui-focused": {
                    color: "var(--text-primary)",
                  },
                }}
              >
                Include channel pricing
              </InputLabel>

              <Select
                labelId="include-channel-pricing-label"
                label="Include channel pricing"
                value={filters.includeChannelPricing ?? ""}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    includeChannelPricing: event.target.value,
                  }))
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      background: "var(--background-primary)",
                      color: "var(--text-primary)",
                      "& .MuiMenuItem-root": {
                        background: "var(--background-primary)",
                        color: "var(--text-primary)",
                      },
                      "& .MuiMenuItem-root:hover": {
                        background: "var(--background-secondary)",
                      },
                      "& .MuiMenuItem-root.Mui-selected": {
                        background: "var(--background-secondary)",
                      },
                      "& .MuiMenuItem-root.Mui-selected:hover": {
                        background: "var(--background-secondary)",
                      },
                    },
                  },
                }}
                sx={{
                  background: "var(--background-primary)",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--border-color)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--border-color)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--text-primary)",
                  },
                  "& .MuiSelect-select": {
                    color: "var(--text-primary)",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "var(--text-primary)",
                  },
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="1">Included</MenuItem>
                <MenuItem value="0">Excluded</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
        <section className="space-y-3">
          <div>
            <h3>Communication channel pricing</h3>
            <p className="text-sm text-textSecondary">
              Current reference prices used when channel pricing is included.
            </p>
          </div>
          <CustomTable
            rows={filteredChannelPricing}
            columns={channelColumns}
            pageSize={5}
            pageSizeOptions={[5]}
            searchable={false}
            hidePagination={true}
            height="360px"
            emptyMessage="No communication channel pricing found."
          />
        </section>

        <section className="space-y-3">
          <div>
            <h3>User pricing hierarchy</h3>
            <p className="text-sm text-textSecondary">
              Click the plus icon to expand a user and view its configured
              entity pricing.
            </p>
          </div>
          <div className="overflow-hidden rounded-2xl border border-borderColor bg-backgroundSecondary">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px] text-left text-sm">
                <thead className="bg-backgroundPrimary text-xs uppercase tracking-wide text-textSecondary">
                  <tr>
                    <th className="w-16 px-4 py-4"> </th>
                    <th className="px-4 py-4">User / business</th>
                    <th className="px-4 py-4">Currency</th>
                    <th className="px-4 py-4">Configured channels</th>
                    <th className="px-4 py-4">Pricing rules</th>
                    <th className="px-4 py-4">Total configured coins</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedUsers.length === 0 && (
                    <tr>
                      <td
                        colSpan="6"
                        className="p-12 text-center text-textSecondary"
                      >
                        No user pricing configuration found.
                      </td>
                    </tr>
                  )}
                  {groupedUsers.map((user) => {
                    const expanded = expandedUsers[user.key] === true;
                    const total = user.rows.reduce(
                      (sum, row) => sum + Number(row.total_coins ?? 0),
                      0,
                    );
                    const channels = [
                      ...new Set(
                        user.rows
                          .map((row) => row.channel_name)
                          .filter(Boolean),
                      ),
                    ];
                    return (
                      <React.Fragment key={user.key}>
                        <tr
                          className={`border-t border-borderColor transition-colors ${expanded ? "bg-backgroundPrimary" : "hover:bg-backgroundPrimary"}`}
                        >
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              aria-label={`${expanded ? "Collapse" : "Expand"} ${user.corporateName}`}
                              onClick={() => toggleUser(user.key)}
                              className="relative flex h-9 w-9 items-center justify-center rounded-full border-2 border-accent bg-accent p-0 text-white shadow-sm transition-all hover:scale-105 hover:bg-accent/80 focus:outline-none focus:ring-2 focus:ring-accent/50"
                            >
                              <span
                                aria-hidden="true"
                                className="absolute inset-0 flex items-center justify-center pb-px font-mono text-[21px] font-bold leading-none"
                              >
                                {expanded ? "−" : "+"}
                              </span>
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <span className="rounded-lg bg-accent/15 p-2 text-accent">
                                <MdCheckCircle size={20} />
                              </span>
                              <span>
                                <span className="block font-semibold text-textPrimary">
                                  {user.corporateName}
                                </span>
                                <span className="block text-xs text-textSecondary">
                                  {user.businessName ||
                                    user.username ||
                                    `User #${user.userId}`}
                                </span>
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 font-medium text-textPrimary">
                            {user.symbol || user.currency || "—"}{" "}
                            <span className="text-xs text-textSecondary">
                              {user.currency || ""}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {channels.map((channel) => (
                                <Chip
                                  key={channel}
                                  label={channel}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    color: "var(--text-primary)",
                                    borderColor: "var(--border-color)",
                                  }}
                                />
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-textPrimary">
                            {user.rows.length}
                          </td>
                          <td className="px-4 py-3 font-bold text-accent">
                            {number(total)} coins
                          </td>
                        </tr>
                        {expanded && (
                          <tr className="border-t border-borderColor bg-backgroundPrimary">
                            <td colSpan="6" className="p-0">
                              <div className="ml-12 border-l-2 border-accent/40 py-3 pl-5 pr-5">
                                <div className="mb-3 flex items-center justify-between">
                                  <p className="text-xs font-bold uppercase tracking-wider text-textSecondary">
                                    Detailed configuration
                                  </p>
                                  <span className="text-xs text-textSecondary">
                                    {user.rows.length} entity rules
                                  </span>
                                </div>
                                <table className="w-full min-w-[980px] text-left text-sm">
                                  <thead className="text-xs uppercase tracking-wide text-textSecondary">
                                    <tr>
                                      <th className="px-3 py-2">
                                        Channel / country
                                      </th>
                                      <th className="px-3 py-2">Entity type</th>
                                      <th className="px-3 py-2">
                                        Platform fee
                                      </th>
                                      <th className="px-3 py-2">
                                        Channel pricing
                                      </th>
                                      <th className="px-3 py-2">
                                        Channel amount
                                      </th>
                                      <th className="px-3 py-2">Total coins</th>
                                      <th className="px-3 py-2">
                                        Effective from
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {user.rows.map((row) => (
                                      <tr
                                        key={row.platform_fee_id}
                                        className="border-t border-borderColor text-textPrimary"
                                      >
                                        <td className="px-3 py-3">
                                          <span className="font-semibold">
                                            {row.channel_name}
                                          </span>
                                          <span className="block text-xs text-textSecondary">
                                            {row.country_code} ·{" "}
                                            {row.country_code_iso3}
                                          </span>
                                        </td>
                                        <td className="px-3 py-3">
                                          {row.entity_type || "—"}
                                        </td>
                                        <td className="px-3 py-3 font-semibold">
                                          {number(row.platform_fee_coins)}
                                        </td>
                                        <td className="px-3 py-3">
                                          <Chip
                                            label={
                                              Number(
                                                row.is_include_channel_pricing,
                                              ) === 1
                                                ? "Included"
                                                : "Excluded"
                                            }
                                            size="small"
                                            color={
                                              Number(
                                                row.is_include_channel_pricing,
                                              ) === 1
                                                ? "success"
                                                : "default"
                                            }
                                          />
                                        </td>
                                        <td className="px-3 py-3">
                                          {Number(
                                            row.is_include_channel_pricing,
                                          ) === 1
                                            ? `${money(row.channel_price, row.user_currency_symbol || row.channel_currency_symbol, row.user_currency || row.channel_currency)} · ${number(row.channel_price_coins)} coins`
                                            : "—"}
                                        </td>
                                        <td className="px-3 py-3 font-bold text-accent">
                                          {number(row.total_coins)}
                                        </td>
                                        <td className="px-3 py-3 text-textSecondary">
                                          {row.platform_fee_wef
                                            ? isoTimestampToReadable(
                                                row.platform_fee_wef,
                                              )
                                            : "—"}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </section>
    </main>
  );
};

export default Pricing;
